<script setup lang="ts">
import { ref, watch, computed } from 'vue';
import { useForceUpdateDeliveryStatus } from '../model/useForceUpdateDeliveryStatus';
import type { ForceUpdateTargetStatus } from '../api/forceUpdateDeliveryStatus';
import { ADMIN_DELIVERY_STATUS_LABELS } from '@/shared/config/deliveryStatusLabels';
import type { components } from '@/shared/api/autogen/schema';

type AdminDeliveryStatus = components['schemas']['AdminDeliveryStatus'];

export type Props = {
    /** モーダルの開閉状態 */
    modelValue: boolean;
    /** 申し込みID */
    applicationId?: string;
    /** 現在の配送ステータス */
    currentStatus?: AdminDeliveryStatus;
};

const props = withDefaults(defineProps<Props>(), {
    modelValue: false,
    applicationId: '',
    currentStatus: undefined,
});

const emit = defineEmits<{
    'update:modelValue': [boolean];
    'status-updated': [];
}>();

// ローカル状態管理
const isOpen = ref(props.modelValue);
const selectedStatus = ref<ForceUpdateTargetStatus | ''>('');

// カスタムフック
const { isLoading, error, execute, clearError } = useForceUpdateDeliveryStatus();

// 全ての配送ステータスオプション
const targetStatusOptions: { value: ForceUpdateTargetStatus; label: string; color: string; icon: string }[] = [
    {
        value: 'applied',
        label: ADMIN_DELIVERY_STATUS_LABELS.applied,
        color: 'blue-grey',
        icon: 'mdi-file-document-outline',
    },
    {
        value: 'airport_counter_received',
        label: ADMIN_DELIVERY_STATUS_LABELS.airport_counter_received,
        color: 'blue',
        icon: 'mdi-airplane',
    },
    {
        value: 'in_transit',
        label: ADMIN_DELIVERY_STATUS_LABELS.in_transit,
        color: 'indigo',
        icon: 'mdi-truck-delivery',
    },
    {
        value: 'hotel_arrived',
        label: ADMIN_DELIVERY_STATUS_LABELS.hotel_arrived,
        color: 'teal',
        icon: 'mdi-home-city',
    },
    {
        value: 'hotel_checked',
        label: ADMIN_DELIVERY_STATUS_LABELS.hotel_checked,
        color: 'green',
        icon: 'mdi-clipboard-check',
    },
    {
        value: 'delivery_completed',
        label: ADMIN_DELIVERY_STATUS_LABELS.delivery_completed,
        color: 'success',
        icon: 'mdi-check-circle',
    },
    {
        value: 'cancelled',
        label: ADMIN_DELIVERY_STATUS_LABELS.cancelled,
        color: 'error',
        icon: 'mdi-cancel',
    },
    {
        value: 'delayed',
        label: ADMIN_DELIVERY_STATUS_LABELS.delayed,
        color: 'warning',
        icon: 'mdi-alert-circle',
    },
];

// 現在のステータスかどうかを判定
const isCurrentStatus = (status: ForceUpdateTargetStatus) => {
    return status === props.currentStatus;
};

// modelValueの変更を監視
watch(
    () => props.modelValue,
    (newValue) => {
        isOpen.value = newValue;
        if (newValue) {
            resetForm();
        }
    }
);

// isOpenの変更を親に通知
watch(isOpen, (newValue) => {
    emit('update:modelValue', newValue);
});

// フォームリセット
const resetForm = () => {
    selectedStatus.value = '';
    clearError();
};

// モーダルを閉じる
const closeModal = () => {
    isOpen.value = false;
    resetForm();
};

// ステータス変更実行
const handleUpdateStatus = async () => {
    if (!props.applicationId || !selectedStatus.value) {
        return;
    }

    const success = await execute(props.applicationId, selectedStatus.value);

    if (success) {
        emit('status-updated');
        closeModal();
    }
};

// バリデーション
const isValidForm = computed(() => {
    return selectedStatus.value && props.applicationId;
});

// 選択されたステータスの情報
const selectedStatusInfo = computed(() => {
    return targetStatusOptions.find((option) => option.value === selectedStatus.value);
});

// 現在のステータスラベル
const currentStatusLabel = computed(() => {
    if (!props.currentStatus) return '';
    return ADMIN_DELIVERY_STATUS_LABELS[props.currentStatus] ?? props.currentStatus;
});

// 選択されたステータスの色（v-alertのtype用）
const selectedAlertType = computed((): 'success' | 'error' | 'warning' | 'info' => {
    const color = selectedStatusInfo.value?.color;
    if (color === 'success' || color === 'error' || color === 'warning') {
        return color;
    }
    return 'info';
});
</script>

<template>
    <v-dialog v-model="isOpen" max-width="500" persistent>
        <v-card>
            <v-card-title class="d-flex justify-space-between align-center bg-warning text-white">
                <div class="d-flex align-center">
                    <v-icon class="me-2">mdi-swap-horizontal</v-icon>
                    <span>配送ステータス強制変更</span>
                </div>
                <v-btn icon="mdi-close" variant="text" color="white" size="small" @click="closeModal" />
            </v-card-title>

            <v-card-text class="pa-6">
                <!-- 警告 -->
                <v-alert type="warning" variant="outlined" class="mb-4">
                    <v-alert-title>注意</v-alert-title>
                    この操作は配送ステータスを強制的に変更します。変更内容はSlackに通知されます。
                </v-alert>

                <!-- エラーメッセージ -->
                <v-alert
                    v-if="error"
                    type="error"
                    variant="outlined"
                    class="mb-4"
                    :text="error"
                    closable
                    @click:close="clearError"
                />
                <v-alert
                    v-if="error"
                    type="error"
                    variant="outlined"
                    class="mb-4"
                    :text="error"
                    closable
                    @click:close="clearError"
                />

                <!-- 現在のステータス -->
                <div class="mb-4">
                    <h4 class="text-subtitle-2 text-grey mb-2">現在のステータス</h4>
                    <v-chip color="primary" variant="outlined">
                        {{ currentStatusLabel }}
                    </v-chip>
                </div>

                <!-- ステータス選択 -->
                <div class="mb-4">
                    <h4 class="text-subtitle-2 text-grey mb-3">変更先のステータス</h4>
                    <v-radio-group v-model="selectedStatus" hide-details>
                        <v-card
                            v-for="option in targetStatusOptions"
                            :key="option.value"
                            variant="outlined"
                            :color="selectedStatus === option.value ? option.color : 'default'"
                            class="status-option mb-2"
                            :class="{
                                'selected-option': selectedStatus === option.value,
                                'disabled-option': isCurrentStatus(option.value),
                            }"
                            @click="!isCurrentStatus(option.value) && (selectedStatus = option.value)"
                        >
                            <v-card-text class="pa-3 d-flex align-center">
                                <v-radio
                                    :value="option.value"
                                    :color="option.color"
                                    :disabled="isCurrentStatus(option.value)"
                                    hide-details
                                    class="grow-0"
                                />
                                <v-icon
                                    :color="isCurrentStatus(option.value) ? 'grey' : option.color"
                                    class="status-icon"
                                >
                                    {{ option.icon }}
                                </v-icon>
                                <span
                                    class="font-weight-medium"
                                    :class="{ 'text-grey': isCurrentStatus(option.value) }"
                                >
                                    {{ option.label }}
                                </span>
                                <v-chip
                                    v-if="isCurrentStatus(option.value)"
                                    size="x-small"
                                    color="grey"
                                    variant="tonal"
                                    class="ml-2"
                                >
                                    現在
                                </v-chip>
                            </v-card-text>
                        </v-card>
                    </v-radio-group>
                </div>

                <!-- 確認メッセージ -->
                <v-alert v-if="selectedStatusInfo" :type="selectedAlertType" variant="tonal" class="mb-0">
                    <strong>{{ currentStatusLabel }}</strong> →
                    <strong>{{ selectedStatusInfo.label }}</strong> に変更します。
                </v-alert>
            </v-card-text>

            <v-card-actions class="pa-6 pt-0 ga-3">
                <v-spacer />
                <v-btn color="grey" variant="outlined" class="modal-btn" @click="closeModal" :disabled="isLoading">
                    キャンセル
                </v-btn>
                <v-btn
                    :color="selectedStatusInfo?.color ?? 'primary'"
                    class="modal-btn"
                    :disabled="!isValidForm || isLoading"
                    :loading="isLoading"
                    @click="handleUpdateStatus"
                >
                    変更を実行
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<style scoped>
.status-option {
    cursor: pointer;
    transition: all 0.2s ease;
}

.status-option:hover:not(.disabled-option) {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
}

.selected-option {
    border-width: 2px;
}

.disabled-option {
    cursor: not-allowed;
    opacity: 0.6;
    background-color: #f5f5f5;
}

.status-icon {
    width: 32px;
    margin-right: 8px;
}

.modal-btn {
    min-height: 44px;
    padding: 0 20px;
}
</style>
